import React, { useState, useEffect, useContext } from "react";
import {
  TextButton,
  Dropdown,
  ToggleSwitch,
  useTranslations,
  Tooltip,
} from "@gf-easy-8/grmnext-foundation";
import { ThemeContext } from "styled-components";
import { useDispatch } from "react-redux";
import { Slider } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";

import useStatusBar from "provider/StatusBarProvider";
import usePermission from "provider/PermissionProvider";
import useSnackBar from "provider/SnackBarProvider";
import ArrowLeftIcon from "components/v2/icons/generated/ArrowLeftOutline";
import InfoCircleOutline from "components/v2/icons/generated/InfoCircleOutline";
import { Label, SpinnerLoader } from "components/v2";
import { AppDispatch, Product, UserProductPermission } from "types";
import { UserFormData } from "../type";
import {
  getProductRolesAccessOptions,
  getProductOptions,
  getProductRolesAccess,
  updateProductRolesAccess,
} from "./actions";
import { getUsersV2 } from "../../actions";
import { transformArrayProperties } from "helper/utils";
import {
  ROLENAMES,
  CONFIGURATION_KEY,
  FIRMWARE_KEY,
  USER_MANAGEMENT_KEY,
  ACCESSNAMES,
  FULL_ACCESS_KEY,
  READ_ACCESS_KEY,
} from "provider/PermissionProvider/permissions";
import { FormMode } from "constant";
import { ProductAccessContainer, ProductAccessCoreContainer, ProductAccessRowContainer, ProductAccessRowLeftContainer, ProductAccessRowRightLabelsContainer, ProductAccessRowRightLabelContainer, ProductAccessRowRightContainer, IconInfoTooltipContainer, ProductAccessSubAccessContainer, StyledPrimarySaveButton } from "./ProductAccessDetails.styled";
import { Access, Role } from "./ProductAccessDetails.types";
import SubAccess from "./SubAccess";

const ProductAccessDetails = ({
  user,
  form,
  formData,
  setFormData,
  setShowProductAccess,
  selectedProduct,
  setSelectedProduct,
  setRefreshUser,
}: {
  user: UserFormData;
  form: { disabled: boolean; mode: number };
  formData: UserFormData;
  setFormData: React.Dispatch<React.SetStateAction<UserFormData>>;
  setShowProductAccess: React.Dispatch<React.SetStateAction<boolean>>;
  selectedProduct: number;
  setSelectedProduct: React.Dispatch<React.SetStateAction<number>>;
  setRefreshUser: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { getTranslation } = useTranslations();
  const dispatch: AppDispatch = useDispatch();
  const theme = useContext(ThemeContext);
  const { statusBarContext } = useStatusBar();
  const { setStatusBarToLoading, setStatusBarToHealthy, setStatusBarToError } =
    statusBarContext.setStatusBar;
  const { permissionContext } = usePermission();
  const { hasFullPermission, permissions } = permissionContext.permissions;
  const [refreshPermissions] = permissionContext.refresh;
  const [currentUser] = permissionContext.user;
  const { snackBarContext } = useSnackBar();
  const { pushSuccessMessage, pushErrorMessage } = snackBarContext.modify;

  const [userCoreRoles, setUserCoreRoles] = useState<Role[]>([]);
  const [userOtherRoles, setUserOtherRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasFullAccess, setHasFullAccess] = useState<boolean>(false);
  const [selectedProductRoleAccessIds, setSelectedProductRoleAccessIds] =
    useState<string[]>([]);
  const [productOptions, setProductOptionsInternal] = useState<Product[]>([]);
  const [hasAccesses, setHasAccesses] = useState<boolean>(false);
  const setProductOptions = (products: Product[]) => {
    const newProducts = [{ productId: -1, productName: "-" }, ...products];
    setProductOptionsInternal(newProducts);
  };

  const getProductName = (products: Product[], productId: number) => {
    let productName: string = "";

    const product = products.find((product) => product.productId === productId);
    if (product) {
      productName = product.productName || "";
    }

    return productName;
  };

  const coreRoles = [
    ROLENAMES[CONFIGURATION_KEY].id,
    ROLENAMES[FIRMWARE_KEY].id,
    ROLENAMES[USER_MANAGEMENT_KEY].id,
  ];

  const isProductExists = (
    products: Product[] | null | undefined,
    selectedProduct: number
  ) => {
    return (
      products &&
      products.some((product: Product) => product.productId === selectedProduct)
    );
  };

  useEffect(() => {
    setIsLoading(true);
    setStatusBarToLoading();

    if (hasFullPermission(selectedProduct, USER_MANAGEMENT_KEY)) {
      setHasFullAccess(true);
    } else {
      setHasFullAccess(false);
    }

    dispatch(getProductRolesAccessOptions(selectedProduct)).then(
      (roles: any) => {
        if (roles && roles.length > 0) {
          let tempUserCoreRoles: Role[] = [];
          let tempUserOtherRoles: Role[] = [];
          roles.map((role: Role) => {
            if (coreRoles.includes(role.roleId)) {
              tempUserCoreRoles = [...tempUserCoreRoles, role];
            } else {
              tempUserOtherRoles = [...tempUserOtherRoles, role];
            }
          });

          setUserCoreRoles(tempUserCoreRoles);
          setUserOtherRoles(tempUserOtherRoles);
        }

        dispatch(getProductOptions(ROLENAMES[USER_MANAGEMENT_KEY].id)).then(
          (products: Product[]) => {
            products.sort((a: Product, b: Product) => {
              if (a.productName && b.productName) {
                return a.productName.localeCompare(b.productName);
              }
              return 0;
            });
            setProductOptions(products);

            let currentProdcutIdPermissions = permissions.map(
              (permission: UserProductPermission) => permission.productId
            );
            currentProdcutIdPermissions = [-1, ...currentProdcutIdPermissions];

            const hasNewProducts = products.some(
              (product: Product) =>
                !currentProdcutIdPermissions.includes(product.productId)
            );

            if (hasNewProducts) {
              refreshPermissions();
            }

            let refreshedSelectedProductRoleAccessIds: string[] = [];
            if (form.mode === FormMode.Add) {
              if (isProductExists(formData.products, selectedProduct)) {
                refreshedSelectedProductRoleAccessIds =
                  formData && formData.products
                    ? formData.products.filter(
                        (product) => product.productId === selectedProduct
                      )[0].productRoleAccessIds!
                    : [];
                setSelectedProductRoleAccessIds(
                  refreshedSelectedProductRoleAccessIds
                );
              } else {
                setSelectedProductRoleAccessIds([]);
              }
              setIsLoading(false);
              setStatusBarToHealthy();
            } else if (form.mode === FormMode.Edit && selectedProduct !== -1) {
              dispatch(
                getProductRolesAccess(user.emailAddress, selectedProduct)
              ).then((response: { productRoleAccessIds: string[] }) => {
                refreshedSelectedProductRoleAccessIds =
                  response.productRoleAccessIds;
                setSelectedProductRoleAccessIds(
                  refreshedSelectedProductRoleAccessIds
                );
                setIsLoading(false);
                setStatusBarToHealthy();
              });
            } else {
              setIsLoading(false);
              setStatusBarToHealthy();
            }
          }
        );
      }
    );
  }, [selectedProduct]);

  useEffect(() => {
    if (selectedProductRoleAccessIds.length > 0) {
      setHasAccesses(true);
    } else {
      setHasAccesses(false);
    }
  }, [selectedProductRoleAccessIds]);

  const StyledSlider = withStyles({
    root: {
      color: theme.colours.button.primary.background,
      height: 5,
    },
    thumb: {
      height: 15,
      width: 15,
      marginTop: -5,
    },
    track: {
      height: 5,
    },
    rail: {
      height: 5,
    },
  })(Slider);

  const getSliderValue: (accesses: Access[]) => number = (
    accesses: Access[]
  ) => {
    let value = 0;

    const readProductRoleAccessId = accesses.filter(
      (access) => access.accessId === ACCESSNAMES[READ_ACCESS_KEY].id
    )[0].productRoleAccessId;
    if (selectedProductRoleAccessIds.includes(readProductRoleAccessId)) {
      value = 1;
    }
    const fullProductRoleAccessId = accesses.filter(
      (access) => access.accessId === ACCESSNAMES[FULL_ACCESS_KEY].id
    )[0].productRoleAccessId;
    if (selectedProductRoleAccessIds.includes(fullProductRoleAccessId)) {
      value = 2;
    }
    return value;
  };

  const onSliderHandleChange =
    (accesses: Access[]) => (event: any, newValue: number | number[]) => {
      const accessesProductRoleAccessIds = accesses.map(
        (access) => access.productRoleAccessId
      );
      let newSelectedProductRoleAccessIds = [...selectedProductRoleAccessIds];
      newSelectedProductRoleAccessIds = newSelectedProductRoleAccessIds.filter(
        (id: string) => !accessesProductRoleAccessIds.includes(id)
      );

      if (
        newValue === 1 &&
        accesses.some(
          (access) => access.accessId === ACCESSNAMES[READ_ACCESS_KEY].id
        )
      ) {
        const newProductRoleAccessId = accesses.filter(
          (access) => access.accessId === ACCESSNAMES[READ_ACCESS_KEY].id
        )[0].productRoleAccessId;
        newSelectedProductRoleAccessIds = [
          ...newSelectedProductRoleAccessIds,
          newProductRoleAccessId,
        ];
      } else if (
        newValue === 2 &&
        accesses.some(
          (access) => access.accessId === ACCESSNAMES[FULL_ACCESS_KEY].id
        )
      ) {
        const newProductRoleAccessId = accesses.filter(
          (access) => access.accessId === ACCESSNAMES[FULL_ACCESS_KEY].id
        )[0].productRoleAccessId;
        newSelectedProductRoleAccessIds = [
          ...newSelectedProductRoleAccessIds,
          newProductRoleAccessId,
        ];
      }
      setSelectedProductRoleAccessIds(newSelectedProductRoleAccessIds);
    };

  const onToggleChange =
    (productRoleAccessId: string, accesses?: Access[]) =>
    () => {
      let newSelectedProductRoleAccessIds = [...selectedProductRoleAccessIds];
      if (newSelectedProductRoleAccessIds.includes(productRoleAccessId)) {
        newSelectedProductRoleAccessIds =
          newSelectedProductRoleAccessIds.filter(
            (id: string) => id !== productRoleAccessId
          );
      } else {
        newSelectedProductRoleAccessIds = [
          ...newSelectedProductRoleAccessIds,
          productRoleAccessId,
        ];
      }

      // remove subAccesses if parent is being unchecked
      const subAccessIdsToRemove = new Set<string>();
      getSubAccessIdsToRemove(accesses ?? [], subAccessIdsToRemove);
      newSelectedProductRoleAccessIds =
        newSelectedProductRoleAccessIds.filter(
          (id: string) => !subAccessIdsToRemove.has(id)
        );

      setSelectedProductRoleAccessIds(newSelectedProductRoleAccessIds);
    };

    
  const getSubAccessIdsToRemove = (subAccesses: Access[], subAccessIdsToRemove: Set<string>) => {
    if (Array.isArray(subAccesses) && subAccesses.length > 0) {
      subAccesses.forEach((subAccess) => {
        subAccessIdsToRemove.add(subAccess.productRoleAccessId)
        getSubAccessIdsToRemove(subAccess.subAccesses ?? [], subAccessIdsToRemove) // recursive for nested subAccesses
      });
    }

    return subAccessIdsToRemove;
  }

  const onAdd = () => {
    const newUserProducts =
      formData &&
      formData.userProducts &&
      formData.userProducts.filter(
        (userProduct) => userProduct.productId !== selectedProduct
      );
    const newProducts =
      formData &&
      formData.products &&
      formData.products.filter(
        (product) => product.productId !== selectedProduct
      );
    setFormData({
      ...formData,
      products: [
        ...(newProducts ? newProducts : []),
        {
          productId: selectedProduct,
          productRoleAccessIds: selectedProductRoleAccessIds,
        },
      ],
      userProducts: [
        ...(newUserProducts ? newUserProducts : []),
        {
          productId: selectedProduct,
          productName: getProductName(productOptions, selectedProduct),
        },
      ],
    });

    setShowProductAccess(false);
  };

  const onSave = () => {
    setIsLoading(true);
    setStatusBarToLoading();
    dispatch(
      updateProductRolesAccess(user.emailAddress, selectedProduct, {
        productRoleAccessIds: selectedProductRoleAccessIds,
      })
    ).then((isSuccessful: boolean) => {
      setIsLoading(false);
      if (isSuccessful) {
        setStatusBarToHealthy();
        pushSuccessMessage(getTranslation("USER_MANAGEMENT:USER_UPDATED"));
      } else {
        setStatusBarToError();
        pushErrorMessage(getTranslation("USER_MANAGEMENT:USER_UPDATE_ERROR"));
      }
      if (currentUser === user.emailAddress) {
        refreshPermissions();
        dispatch(getUsersV2());
      }
    });
  };

  return (
    <>
      <TextButton
        onClick={() => {
          if (form.mode === FormMode.Edit) {
            setRefreshUser(new Date().getTime());
          }
          setShowProductAccess(false);
        }}
        primaryColor={true}
        isUnderlined={true}
        hasNormalText={true}
        icon={<ArrowLeftIcon />}
      >
        {getTranslation("GLOBAL:BACK")}
      </TextButton>
      <ProductAccessContainer>
        <Dropdown
          options={transformArrayProperties(productOptions, [
            { from: "productName", to: "label" },
            { from: "productId", to: "value" },
          ])}
          title={getTranslation("USER_MANAGEMENT:PRODUCT")}
          onChange={setSelectedProduct}
          value={selectedProduct}
        />
        {isLoading ? (
          <SpinnerLoader />
        ) : (
          <>
            <div id="core-roles-div">
              <hr
                style={{ borderColor: theme.colours.global.lightFadedWhite }}
              />
              <ProductAccessCoreContainer>
                <ProductAccessRowContainer>
                  <ProductAccessRowLeftContainer></ProductAccessRowLeftContainer>
                  <ProductAccessRowRightLabelsContainer>
                    <ProductAccessRowRightLabelContainer>
                      {getTranslation("USER_MANAGEMENT:NO_ACCESS")}
                    </ProductAccessRowRightLabelContainer>
                    <ProductAccessRowRightLabelContainer>
                      {getTranslation("USER_MANAGEMENT:READ_ACCESS")}
                    </ProductAccessRowRightLabelContainer>
                    <ProductAccessRowRightLabelContainer>
                      {getTranslation("USER_MANAGEMENT:FULL_ACCESS")}
                    </ProductAccessRowRightLabelContainer>
                  </ProductAccessRowRightLabelsContainer>
                </ProductAccessRowContainer>
                {userCoreRoles &&
                  userCoreRoles.map((role: Role, key: number) => (
                    <ProductAccessRowContainer
                      key={`productAccessRowContianer${key}`}
                    >
                      <ProductAccessRowLeftContainer>
                        <Label>{role.roleName}</Label>
                      </ProductAccessRowLeftContainer>
                      <ProductAccessRowRightContainer>
                        <StyledSlider
                          id={role.roleId}
                          aria-label={role.roleName}
                          defaultValue={0}
                          step={1}
                          marks
                          min={0}
                          max={role.accesses.length}
                          disabled={role.productId === -1 || !hasFullAccess}
                          value={getSliderValue(role.accesses)}
                          onChange={onSliderHandleChange(role.accesses)}
                        />
                      </ProductAccessRowRightContainer>
                    </ProductAccessRowContainer>
                  ))}
              </ProductAccessCoreContainer>
            </div>
            {userOtherRoles && userOtherRoles.length > 0 && (
              <div id="other-roles-div">
                <hr
                  style={{ borderColor: theme.colours.global.lightFadedWhite }}
                />
                <ProductAccessCoreContainer>
                  {userOtherRoles.map((role: Role, key: number) => {
                    return (
                      role.accesses &&
                      role.accesses[0] && (
                        <div key={`productAccessRowContianer${key}`}>
                          <ProductAccessRowContainer
                            key={`productAccessRowContianer${key}`}
                          >
                            <ProductAccessRowLeftContainer>
                              <Label>{role.roleName}</Label>
                            </ProductAccessRowLeftContainer>
                            <ProductAccessRowRightContainer>
                              <ToggleSwitch
                                checked={selectedProductRoleAccessIds.includes(
                                  role.accesses[0].productRoleAccessId
                                )}
                                onChange={onToggleChange(
                                  role.accesses[0].productRoleAccessId,
                                  role.accesses[0].subAccesses
                                    ? role.accesses[0].subAccesses
                                    : []
                                )}
                                disabled={!hasFullAccess}
                              />
                              {role.accesses[0] &&
                                role.accesses[0].subAccesses &&
                                role.accesses[0].subAccesses.length > 0 &&
                                selectedProductRoleAccessIds.includes(
                                  role.accesses[0].productRoleAccessId
                                ) && (
                                  <IconInfoTooltipContainer>
                                    <Tooltip
                                      content={getTranslation(
                                        "USER_MANAGEMENT:ROLE_INFO_TOOLTIP"
                                      )}
                                    >
                                      <InfoCircleOutline />
                                    </Tooltip>
                                  </IconInfoTooltipContainer>
                                )}
                            </ProductAccessRowRightContainer>
                          </ProductAccessRowContainer>
                          {selectedProductRoleAccessIds.includes(
                            role.accesses[0].productRoleAccessId
                          ) &&
                            role.accesses[0].subAccesses &&
                            role.accesses[0].subAccesses.length > 0 && (
                              <ProductAccessRowContainer
                                key={`productSubAccessRowContianer${key}`}
                              >
                                <ProductAccessRowLeftContainer></ProductAccessRowLeftContainer>
                                <ProductAccessRowRightContainer>
                                  <ProductAccessSubAccessContainer>
                                    {role.accesses[0].subAccesses.map(
                                      (subaccess: Access, subkey: number) => 
                                        (<SubAccess
                                            subAccess={subaccess}
                                            subAccessKey={subkey}
                                            selectedProductRoleAccessIds={selectedProductRoleAccessIds}
                                            onToggleChange={onToggleChange}
                                            hasFullAccess={hasFullAccess}
                                            parentKey={key}
                                            parentProductRoleAccessId={role.accesses[0].productRoleAccessId}
                                          />
                                        )
                                    )}
                                  </ProductAccessSubAccessContainer>
                                </ProductAccessRowRightContainer>
                              </ProductAccessRowContainer>
                            )}
                        </div>
                      )
                    );
                  })}
                </ProductAccessCoreContainer>
              </div>
            )}
          </>
        )}
        {hasFullAccess && form.mode === FormMode.Edit && (
          <StyledPrimarySaveButton
            onClick={onSave}
            disabled={isLoading || !hasAccesses}
          >
            {getTranslation("GLOBAL:SAVE")}
          </StyledPrimarySaveButton>
        )}
        {hasFullAccess && form.mode === FormMode.Add && (
          <StyledPrimarySaveButton onClick={onAdd} disabled={!hasAccesses}>
            {isProductExists(formData.products, selectedProduct)
              ? getTranslation("GLOBAL:UPDATE")
              : getTranslation("GLOBAL:ADD")}
          </StyledPrimarySaveButton>
        )}
      </ProductAccessContainer>
    </>
  );
};

export default ProductAccessDetails;
